/**
 * ================================================================================================
 *  THE WORD THE SERVER STILL SENDS. **Section 2, 2026-10-26.**
 * ================================================================================================
 *
 * The Owner:
 *
 *   > *"הסר מהממשק את המונח ״משרד״. המערכת אינה מיועדת רק למשרדים."*
 *
 * and, in the same breath, the limit that decides how:
 *
 *   > *"זה שינוי במלל הממשק; אין צורך לשנות מזהים פנימיים או סכמת נתונים רק לצורך שינוי השמות."*
 *
 * ── WHY THIS FILE HAS TO EXIST AT ALL ───────────────────────────────────────────────────────────
 *
 * Every Hebrew string this browser OWNS was rewritten in place. Some of the Hebrew it renders is
 * not its own: capability labels, billing grain names and a few other reference rows are read from
 * the database, where they were written by `0005__reference_data.up.sql`.
 *
 * Three of those capability labels and one grain name still carry the old word. Changing them at
 * the source means regenerating 0005 -- **a migration that has already run on the Owner's server**
 * -- and he ruled that out twice over: once in the sentence above, and once in *"do not perform a
 * broad cleanup or infrastructure changes to work around a failure."* A migration that rewrites
 * history to fix four nouns is exactly that.
 *
 * So the rename happens where he said it should: in the wording, at the moment it is displayed.
 * The row keeps its value, the schema is untouched, the deployed database is untouched, and the
 * reader sees the product's word.
 *
 * ── WHAT IT DELIBERATELY IS NOT ─────────────────────────────────────────────────────────────────
 *
 * It is NOT a translation layer and nothing may grow into one here. It rewrites ONE noun, in its
 * known Hebrew forms, in text that arrived from the server. A general find-and-replace over
 * server text is how somebody later mangles a COMPANY NAME a customer typed -- so this is never
 * applied to data a person entered, only to reference labels the product itself authored.
 *
 * The distinction is enforced by where it is called, and `organisationWording.test.ts` states the
 * rule it cannot enforce mechanically: a customer's own name passes through untouched because
 * nothing calls this on one.
 */

/**
 * The forms, longest first so a prefix is never half-replaced.
 *
 * `שהמשרד`, `מהמשרד` and `והמשרד` need no rule of their own: each contains `המשרד`, so the
 * one-letter prefix survives the substitution and reads correctly.
 */
const FORMS: readonly (readonly [string, string])[] = [
  ['משרדים', 'ארגונים'],
  ['המשרד', 'הארגון'],
  ['במשרד', 'בארגון'],
  ['למשרד', 'לארגון'],
  ['משרד', 'ארגון'],
];

/**
 * A reference label the SERVER authored, in the product's current wording.
 *
 * Call it on labels the product itself wrote and stored — capability names, grain names, status
 * vocabularies. Never on a name, a note or any other value a person typed.
 */
export function organisationWording(serverLabel: string): string {
  let text = serverLabel;
  for (const [was, is] of FORMS) {
    text = text.split(was).join(is);
  }
  return text;
}

/** True when a label still carries the retired noun — used by the check, and by nothing else. */
export function carriesRetiredWord(text: string): boolean {
  return text.includes('משרד');
}
